/* eslint-disable react-hooks/preserve-manual-memoization */
import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { roundUpToNextHour } from '@/Components/Sensors/SensorUtils.js';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { DefaultTimezone } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  SENSORS,
  DEVICE_STATES,
  RADAR_PRESENCE_TYPES,
  INCONTINENCE_SENSOR_STATES,
  SENSOR_VARIANTS,
} from '@/constants/device';
import DoorClosedIcon from '@/Components/Common/Icons/DoorClosedIcon.jsx';
import DoorOpenIcon from '@/Components/Common/Icons/DoorOpenIcon.jsx';
import FlushActiveIcon from '@/Components/Common/Icons/FlushActiveIcon.jsx';
import FlushInactiveIcon from '@/Components/Common/Icons/FlushInactiveIcon.jsx';
import OccupiedIcon from '@/Components/Common/Icons/OccupiedIcon.jsx';
import VacantIcon from '@/Components/Common/Icons/VacantIcon.jsx';
import DryIcon from '@/Components/Common/Icons/dry.svg';
import PowerInactiveIcon from '@/Components/Common/Icons/power.svg';
import PowerActiveIcon from '@/Components/Common/Icons/power-active.svg';
import WetIcon from '@/Components/Common/Icons/wet.svg';
import { getSensorVariant } from '@/utilities/helpers/commonHelper';
import FireIcon from '@/Components/Common/Icons/fire.svg';
import CookingDoneIcon from '@/Components/Common/Icons/cooking-done.svg';
import './DoorWindowChart.less';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Title, Filler);

const HOURS = (h) => h;

const COLORS = {
  closed: 'rgba(241, 246, 255, 1)',
  open: 'rgba(89, 126, 243, 1)',
  fallDetected: 'rgba(255, 77, 79, 0.2)',
  cookingActive: 'rgba(82, 196, 26, 0.85)',
};

export default function DoorWindowChart({
  segments,
  cookingSegments,
  selectedRange,
  setSelectedRange,
  is24hoursformat,
  loginUserLocale,
  timezone,
  sensorDetails,
}) {
  const isMountedRef = useRef(true);
  const trans = useSelector(translator);

  const [hideLowBoolLegend, setHideLowBoolLegend] = useState(false);
  const [trueLegendTitle, setTrueLegendTitle] = useState();
  const [falseLegendTitle, setFalseLegendTitle] = useState();
  const safeTimezone = timezone?.split(' ')[0] || DefaultTimezone;
  const sensorVariant = useMemo(() => getSensorVariant(sensorDetails), [sensorDetails]);

  useEffect(() => {
    if (sensorDetails?.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR) {
      setTrueLegendTitle(trans('ACTIVE'));
      setFalseLegendTitle(trans('INACTIVE'));
    }
    if (sensorDetails?.devicetype === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR) {
      setTrueLegendTitle(trans('DOOR_OPEN'));
      setFalseLegendTitle(trans('DOOR_CLOSED'));
    }
    if (sensorDetails?.devicetype === SENSORS.SMART_HOME.SMART_PLUG) {
      setTrueLegendTitle(trans('ACTIVE'));
      setFalseLegendTitle(trans('INACTIVE'));
    }
    if (sensorDetails?.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR) {
      if (sensorVariant === SENSOR_VARIANTS.INCONTINENCE) {
        setTrueLegendTitle(trans('WET'));
        setFalseLegendTitle(trans('DRY'));
      } else {
        setTrueLegendTitle(trans('OCCUPIED'));
        setFalseLegendTitle(trans('VACANT'));
      }
    }
    if (sensorDetails?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR) {
      setTrueLegendTitle(trans('OCCUPIED'));
      setFalseLegendTitle(trans('VACANT'));
    }
    if (sensorDetails?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR) {
      setTrueLegendTitle(trans('OCCUPIED'));
      setFalseLegendTitle(trans('VACANT'));
      setHideLowBoolLegend(false);
    }
  }, [sensorDetails, sensorVariant, trans]);

  const { data, options } = useMemo(() => {
    const MS_PER_HOUR = 60 * 60 * 1000;

    const RANGE_CONFIG = {
      today: { hours: 24, step: 3, label: 'day' },
      weekly: { hours: 24 * 7, step: 24, label: 'week' },
      monthly: { hours: 24 * 30, step: 72, label: 'month' },
    };

    const getNowUtc = () => new Date();

    const rawNow = getNowUtc();

    const windowEnd =
      selectedRange === 'today'
        ? roundUpToNextHour(new Date(rawNow.getTime()))
        : roundUpToNextHour(rawNow);

    const rangeConfig = RANGE_CONFIG[selectedRange] || RANGE_CONFIG[1];

    const windowStart = new Date(windowEnd.getTime() - rangeConfig.hours * MS_PER_HOUR);

    const resolveHourToDateInWindow = (hourFloat) => {
      const hh = Math.floor(hourFloat);
      const mm = Math.round((hourFloat % 1) * 60);

      const candidate = new Date(windowEnd);
      candidate.setHours(hh, mm, 0, 0);

      if (candidate > windowEnd) {
        candidate.setTime(candidate.getTime() - 24 * MS_PER_HOUR);
      }

      if (candidate < windowStart) {
        candidate.setTime(candidate.getTime() + 24 * MS_PER_HOUR);
      }

      return new Date(candidate);
    };

    const isCooktop = sensorDetails?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR;

    const mapSegmentsToPoints = (segs, yLabel) =>
      segs
        .map((s) => {
          const startDate = s.startTs ? new Date(s.startTs) : resolveHourToDateInWindow(s.start);
          const rawEndDate = s.endTs ? new Date(s.endTs) : resolveHourToDateInWindow(s.end);
          let endResolved = rawEndDate;
          if (endResolved.getTime() <= startDate.getTime()) {
            endResolved = new Date(endResolved.getTime() + 24 * MS_PER_HOUR);
          }
          const clampedStartMs = Math.max(startDate.getTime(), windowStart.getTime());
          const clampedEndMs = Math.min(endResolved.getTime(), windowEnd.getTime());
          if (clampedEndMs <= clampedStartMs) return null;
          const startHours = (clampedStartMs - windowStart.getTime()) / MS_PER_HOUR;
          const endHours = (clampedEndMs - windowStart.getTime()) / MS_PER_HOUR;
          return {
            x: [HOURS(startHours), HOURS(endHours)],
            y: yLabel,
            state: s.state,
            occupancyState: s.occupancyState,
            zoneName: s.zoneName,
          };
        })
        .filter(Boolean);

    const points = isCooktop
      ? mapSegmentsToPoints(segments, 'presence')
      : segments
          .map((s) => {
            const startDate = s.startTs ? new Date(s.startTs) : resolveHourToDateInWindow(s.start);
            const rawEndDate = s.endTs ? new Date(s.endTs) : resolveHourToDateInWindow(s.end);
            let endResolved = rawEndDate;
            if (endResolved.getTime() <= startDate.getTime()) {
              endResolved = new Date(endResolved.getTime() + 24 * MS_PER_HOUR);
            }
            const clampedStartMs = Math.max(startDate.getTime(), windowStart.getTime());
            const clampedEndMs = Math.min(endResolved.getTime(), windowEnd.getTime());
            if (clampedEndMs <= clampedStartMs) return null;
            const startHours = (clampedStartMs - windowStart.getTime()) / MS_PER_HOUR;
            const endHours = (clampedEndMs - windowStart.getTime()) / MS_PER_HOUR;
            return {
              x: [HOURS(startHours), HOURS(endHours)],
              y: 'timeline',
              state: s.state,
              occupancyState: s.occupancyState,
              zoneName: s.zoneName,
            };
          })
          .filter(Boolean);

    const colors = points.map((p) =>
      sensorDetails?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR
        ? p.state === RADAR_PRESENCE_TYPES.FALL_DETECTED
          ? COLORS.fallDetected
          : p.occupancyState === DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED
            ? COLORS.open
            : COLORS.closed
        : [
              DEVICE_STATES.DOOR_WINDOW_SENSOR.OPEN,
              DEVICE_STATES.FLUSH_SENSOR.FLUSHED,
              DEVICE_STATES.SMART_PLUG.ON,
              DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED,
              INCONTINENCE_SENSOR_STATES.WET,
            ].includes(p.state)
          ? COLORS.open
          : COLORS.closed
    );

    const cookingPoints = isCooktop ? mapSegmentsToPoints(cookingSegments || [], 'cooking') : [];
    const cookingColors = cookingPoints.map((p) =>
      p.state === 'cooking_active' ? COLORS.cookingActive : COLORS.closed
    );

    const allPoints = isCooktop ? [...points, ...cookingPoints] : points;
    const allColors = isCooktop ? [...colors, ...cookingColors] : colors;

    const data = {
      labels: isCooktop ? ['presence', 'cooking'] : ['timeline'],
      datasets: [
        {
          label: 'Timeline',
          data: allPoints,
          backgroundColor: allColors,
          borderRadius: 0,
          barThickness: isCooktop ? 60 : 90,
          categoryPercentage: 1,
          barPercentage: 1,
        },
      ],
    };

    const options = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            title: () => '',
            label: (ctx) => {
              const x = ctx.raw?.x;
              if (!Array.isArray(x)) return '';

              let [start, end] = x;

              // If end is null → ongoing segment
              const effectiveEnd = end ?? rangeConfig.hours;

              const formatDateTime = (h) => {
                const ms = windowStart.getTime() + h * MS_PER_HOUR;
                const d = new Date(ms);

                if (Number.isNaN(d.getTime())) return '—';

                const date = d.toLocaleDateString(loginUserLocale, {
                  month: 'short',
                  day: '2-digit',
                  timeZone: safeTimezone,
                });

                const time = d.toLocaleTimeString(loginUserLocale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: !is24hoursformat,
                  timeZone: safeTimezone,
                });

                return `${date} ${time}`;
              };

              const durationHours = effectiveEnd - start;
              const dHours = Math.floor(durationHours);
              const dMinutes = Math.round((durationHours % 1) * 60);

              const durationLabel = Number.isFinite(durationHours)
                ? `${String(dHours).padStart(2, '0')}:${String(dMinutes).padStart(2, '0')}`
                : '—';

              return [
                `${formatDateTime(start)} → ${end == null ? 'Now' : formatDateTime(end)}`,
                ` Duration: ${durationLabel}`,
                ...(sensorDetails?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR && ctx.raw?.state
                  ? [
                      ` Event: ${trans(String(ctx.raw.state).toUpperCase())}`,
                      ...(ctx.raw?.zoneName ? [` Zone: ${ctx.raw.zoneName}`] : []),
                    ]
                  : []),
              ];
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: rangeConfig.hours,
          ticks: {
            stepSize: rangeConfig.step,
            callback: (v) => {
              const utcMs = windowStart.getTime() + v * MS_PER_HOUR;
              const d = new Date(utcMs);

              if (rangeConfig.label === 'day') {
                return d.toLocaleTimeString(loginUserLocale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: !is24hoursformat,
                  timeZone: safeTimezone,
                });
              }

              if (rangeConfig.label === 'week') {
                return d.toLocaleDateString(loginUserLocale, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  timeZone: safeTimezone,
                });
              }

              // month
              return d.toLocaleDateString(loginUserLocale, {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                timeZone: safeTimezone,
              });
            },
          },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
      },
      layout: { padding: { left: 20, right: 24, top: 12, bottom: 12 } },
    };

    return { data, options };
  }, [
    selectedRange,
    segments,
    cookingSegments,
    safeTimezone,
    is24hoursformat,
    loginUserLocale,
    sensorDetails?.devicetype,
    trans,
  ]);

  const handleRangeChange = useCallback(
    (range) => {
      if (isMountedRef.current && range !== selectedRange) {
        setSelectedRange(range);
      }
    },
    [selectedRange, setSelectedRange]
  );

  const activityHeaderKey =
    selectedRange === 'weekly'
      ? 'WEEKLY_ACTIVITY'
      : selectedRange === 'monthly'
        ? 'MONTHLY_ACTIVITY'
        : 'DAILY_ACTIVITY';

  const TrueLegendIcon =
    sensorDetails?.devicetype === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR ? (
      <DoorOpenIcon />
    ) : sensorDetails?.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR ||
      sensorDetails?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR ||
      sensorDetails?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR ? (
      sensorVariant === SENSOR_VARIANTS.INCONTINENCE ? (
        <WetIcon />
      ) : (
        <OccupiedIcon />
      )
    ) : sensorDetails?.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR ? (
      <FlushActiveIcon />
    ) : sensorDetails?.devicetype === SENSORS.SMART_HOME.SMART_PLUG ? (
      <PowerActiveIcon className="power-active" />
    ) : null;

  const FalseLegendIcon =
    sensorDetails?.devicetype === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR ? (
      <DoorClosedIcon />
    ) : sensorDetails?.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR ||
      sensorDetails?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR ||
      sensorDetails?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR ? (
      sensorVariant === SENSOR_VARIANTS.INCONTINENCE ? (
        <DryIcon />
      ) : (
        <VacantIcon />
      )
    ) : sensorDetails?.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR ? (
      <FlushInactiveIcon />
    ) : sensorDetails?.devicetype === SENSORS.SMART_HOME.SMART_PLUG ? (
      <PowerInactiveIcon className="power-inactive" />
    ) : null;

  const isCooktopChart = sensorDetails?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR;

  const legend = (
    <div className="legend">
      {hideLowBoolLegend ? (
        ''
      ) : (
        <div className="legend-row">
          <span className="legend-dot light" />
          {FalseLegendIcon && <div className="legend-icon light">{FalseLegendIcon}</div>}
          <div className="legend-text">
            <div className="legend-title">{falseLegendTitle}</div>
          </div>
        </div>
      )}
      <div className="legend-row">
        <span className="legend-dot dark" />
        {TrueLegendIcon && <div className="legend-icon dark">{TrueLegendIcon}</div>}
        <div className="legend-text">
          <div className="legend-title">{trueLegendTitle}</div>
        </div>
      </div>
      {isCooktopChart && (
        <>
          <div className="legend-row">
            <span
              className="legend-dot"
              style={{ background: 'rgba(241, 246, 255, 1)', border: '1px solid #d1d5db' }}
            />
            <div className="legend-icon" style={{ opacity: 0.5 }}>
              <CookingDoneIcon style={{ width: 25, height: 25 }} />
            </div>
            <div className="legend-text">
              <div className="legend-title">{trans('INACTIVE')}</div>
            </div>
          </div>
          <div className="legend-row">
            <span className="legend-dot" style={{ background: 'rgba(82, 196, 26, 0.85)' }} />
            <div className="legend-icon cooking-active-icon">
              <FireIcon style={{ width: 16, height: 20 }} />
            </div>
            <div className="legend-text">
              <div className="legend-title" style={{ color: '#52c41a' }}>
                {trans('ACTIVE')}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="card">
      <div className="activity-card">
        <div className="activity-header">
          <h3>{trans(activityHeaderKey)}</h3>
          <div className="environmental-chart-view-block common-chart">
            <div
              className={`environmental-chart-view-option ${selectedRange === 'today' ? 'selected-option' : ''}`}
              onClick={() => handleRangeChange('today')}
            >
              <span
                className={`environmental-chart-view-option-label ${selectedRange === 'today' ? 'selected' : ''}`}
              >
                {trans('DAY')}
              </span>
            </div>
            <div
              className={`environmental-chart-view-option ${selectedRange === 'weekly' ? 'selected-option' : ''}`}
              onClick={() => handleRangeChange('weekly')}
            >
              <span
                className={`environmental-chart-view-option-label ${selectedRange === 'weekly' ? 'selected' : ''}`}
              >
                {trans('WEEK')}
              </span>
            </div>
            <div
              className={`environmental-chart-view-option ${selectedRange === 'monthly' ? 'selected-option' : ''}`}
              onClick={() => handleRangeChange('monthly')}
            >
              <span
                className={`environmental-chart-view-option-label ${selectedRange === 'monthly' ? 'selected' : ''}`}
              >
                {trans('MONTH')}
              </span>
            </div>
          </div>
          {legend}
        </div>

        <div className="chart-wrap">
          <Bar data={data} options={options} />
        </div>
      </div>
    </div>
  );
}
DoorWindowChart.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      end: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      state: PropTypes.string.isRequired,
    })
  ).isRequired,
  cookingSegments: PropTypes.arrayOf(
    PropTypes.shape({
      startTs: PropTypes.string,
      endTs: PropTypes.string,
      state: PropTypes.string,
    })
  ),
  selectedRange: PropTypes.string.isRequired,
  setSelectedRange: PropTypes.func.isRequired,
  is24hoursformat: PropTypes.bool.isRequired,
  loginUserLocale: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  sensorDetails: PropTypes.object.isRequired,
};
